import classNames from 'classnames'
import React from 'react'

import { optimizeMessagingAttachmentUrl } from '../../../../utils/cdnImageUrl'
import { nativePlayerProps } from '../../../../utils/nativeMediaPlayers'
import { renderTypeIcon } from '../../../AttachmentCard'
import { getSourceType } from '../../../AttachmentCard/utils/mimeType'

import type { LinkAttachmentVariant } from './CardShell'

export interface CardThumbnailProps {
  variant: LinkAttachmentVariant
  /** Source URL of the hero image (or poster for video). */
  thumbnailUrl?: string
  /**
   * Playable media URL. When provided alongside a video / audio `mimeType`,
   * the hero region renders a native HTML5 player with controls instead of
   * the static thumbnail / placeholder.
   */
  sourceUrl?: string
  /** Alt text — typically the card's title. */
  title?: string
  /**
   * Drives the placeholder type icon when no `thumbnailUrl` is provided,
   * and selects between image / video / audio rendering when `sourceUrl`
   * is set. Defaults to a generic image icon when unset.
   */
  mimeType?: string
  /** Optional decorations layered into the top corners of the thumbnail. */
  topLeft?: React.ReactNode
  topRight?: React.ReactNode
  /**
   * Renders a neutral pulsing block instead of the image while the link
   * preview is still being fetched.
   */
  isLoading?: boolean
  /**
   * Fired when the hero image fails to load. The owning card clears
   * `thumbnailUrl` (so the type-icon placeholder takes over) and drops the
   * accent colour, which was derived from an image the viewer cannot see.
   */
  onImageError?: () => void
}

const PLACEHOLDER_BG: Record<LinkAttachmentVariant, string> = {
  dark: 'bg-white/10',
  light: 'bg-black/5',
}

const PLACEHOLDER_ICON: Record<LinkAttachmentVariant, string> = {
  dark: 'size-16 text-white/25',
  light: 'size-16 text-black/25',
}

/** Mime + sourceUrl gives us a playable audio attachment. */
export const isPlayableAudio = (mimeType?: string, sourceUrl?: string) =>
  !!sourceUrl && !!mimeType && getSourceType(mimeType) === 'audio'

/**
 * Mime + sourceUrl gives us a playable video or audio attachment. Used by
 * Received to skip wrapping the shell in an interactive `<button>` so the
 * native media controls remain operable.
 */
export const isPlayableMedia = (mimeType?: string, sourceUrl?: string) => {
  if (!sourceUrl || !mimeType) return false
  const source = getSourceType(mimeType)
  return source === 'video' || source === 'audio'
}

/**
 * Background colour the LinkAttachment cards switch to when the source is
 * audio — flat neutral around the native `<audio>` chrome regardless of
 * the dark / light variant. Written as a complete literal because Tailwind
 * only generates utilities it can find verbatim when it scans `dist`, so
 * building the class from a hex constant would emit nothing at all.
 */
export const AUDIO_BG_CLASS = 'bg-[#F2F3F4]'

/**
 * Hero region shown above the card body. Inside the `featured` card's fixed
 * 250px box it takes `flex-1`, absorbing whatever height the chin leaves.
 * Renders, in priority order: a loading placeholder, a native
 * `<video controls>` / `<audio controls>` when `sourceUrl` + mime say so, the
 * thumbnail image, then a type-icon placeholder.
 */
const CardThumbnail: React.FC<CardThumbnailProps> = ({
  variant,
  thumbnailUrl,
  sourceUrl,
  title,
  mimeType = 'image/*',
  topLeft,
  topRight,
  isLoading = false,
  onImageError,
}) => {
  const sourceType = getSourceType(mimeType)
  const isPlayableVideo = !!sourceUrl && sourceType === 'video'
  const optimizedThumbnailUrl = optimizeMessagingAttachmentUrl(thumbnailUrl)

  // `!isLoading` first, deliberately: while the scrape is in flight we do not
  // yet know what the asset is, so honouring a `mimeType` the caller happens to
  // have set already would mount a live player next to `CardBody`'s skeleton
  // bars. The loading branch below owns every hero shape (MES-1349).
  if (!isLoading && isPlayableAudio(mimeType, sourceUrl)) {
    // Audio collapses the hero entirely — the native player sits inside
    // the card chrome with a bit of padding so the card background
    // (typically `bg-[#F2F3F4]`) is visible around it.
    return (
      <div className="p-3">
        <audio
          {...nativePlayerProps}
          src={sourceUrl}
          controls
          preload="metadata"
          className="block w-full"
        >
          <track kind="captions" />
        </audio>
      </div>
    )
  }

  return (
    <div
      className={classNames(
        // `flex-1 min-h-0` inside the shell's 250px column: the hero takes
        // every pixel the chin doesn't, which is 168px at a two-line title
        // and 188px at one.
        'relative min-h-0 w-full flex-1 overflow-hidden',
        isPlayableVideo && 'bg-black'
      )}
    >
      {isLoading ? (
        // Reuses the placeholder fill: a fixed `bg-black/[0.06]` would be
        // invisible on the dark card's dark surface. Stock Tailwind only
        // — `animate-shimmer` exists in this package's own tailwind config but
        // not in either consumer's, so it would compile to nothing in
        // frontyard and profiles while looking right in this Storybook.
        <div
          className={classNames(
            'h-full w-full animate-pulse',
            PLACEHOLDER_BG[variant]
          )}
          aria-hidden
        />
      ) : isPlayableVideo ? (
        <>
          <video
            src={sourceUrl}
            poster={optimizedThumbnailUrl}
            controls
            playsInline
            preload="metadata"
            className="absolute inset-0 h-full w-full object-contain"
          >
            <track kind="captions" />
          </video>
          {/*
            The poster *is* the hero on this branch, and `<video>` raises no
            event when one fails to load — its own `error` is about the media
            resource, not the poster — so the `<img>` below verifies it. A
            `display:none` image is still fetched and still fires `onError`
            (checked in a browser, not assumed), and it dedupes with the
            poster's own request for the same URL, so this costs a DOM node
            rather than a round trip. No `loading="lazy"`: that would defer the
            probe while off screen. Without it a 404 poster left the card and
            its tail accent-painted over a bare `bg-black` box, which is the
            one thing `useChinPalette`'s rule exists to prevent (MES-1349).
          */}
          {optimizedThumbnailUrl && onImageError ? (
            <img
              src={optimizedThumbnailUrl}
              alt=""
              aria-hidden
              onError={onImageError}
              className="hidden"
            />
          ) : null}
        </>
      ) : thumbnailUrl ? (
        <img
          src={optimizedThumbnailUrl ?? thumbnailUrl}
          alt={title ?? ''}
          draggable={false}
          loading="lazy"
          decoding="async"
          onError={onImageError}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div
          className={classNames(
            'flex h-full w-full items-center justify-center',
            PLACEHOLDER_BG[variant]
          )}
        >
          {renderTypeIcon(mimeType, {
            className: PLACEHOLDER_ICON[variant],
            weight: 'regular',
          })}
        </div>
      )}

      {topLeft ? (
        <div className="pointer-events-auto absolute left-3 top-3 z-10">
          {topLeft}
        </div>
      ) : null}
      {topRight ? (
        <div className="pointer-events-auto absolute right-3 top-3 z-10">
          {topRight}
        </div>
      ) : null}
    </div>
  )
}

export default CardThumbnail
